import React, { useState, useEffect } from 'react';
import { FiX, FiFile, FiFileText, FiImage, FiVideo, FiMusic, FiArchive, FiMaximize2, FiMinimize2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { showToast } from '../../components/common/Toaster';
import { learnerDownloadAPI } from '../../api/learnerEndpoints';
import { 
  FileViewerOverlay,
  FileViewerContainer,
  FileViewerHeader,
  FileViewerBody,
  FileViewerFooter,
  FilePreview,
  FileInfo,
  FileTitle,
  FileDescription,
  FileMeta,
  ActionButtons,
  DownloadButton,
  CloseButton,
  FileIcon,
  FileSize,
  FileType,
  PreviewContent,
  DocumentPreviewContent,
  UnsupportedFile,
  LoadingSpinner,
} from './FileViewer.styles';

const FileViewer = ({ 
  isOpen, 
  onClose, 
  file,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false
}) => {
  const [fileUrl, setFileUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (isOpen && file) {
      loadFile();
    }
  }, [isOpen, file]);

  const loadFile = async () => {
    if (!file) return;

    setLoading(true);
    setPreviewError(false);
    
    try {
      const response = await learnerDownloadAPI.getFilePreview(file.id);
      if (response.data.preview_url) {
        setFileUrl(response.data.preview_url);
      } else {
        setPreviewError(true);
      }
    } catch (error) {
      console.error('File loading error:', error);
      setPreviewError(true);
      showToast('error', 'Failed to load file preview');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase() || '';
    
    if (type.includes('pdf')) return <FiFileText />;
    if (type.includes('image')) return <FiImage />;
    if (type.includes('video')) return <FiVideo />;
    if (type.includes('audio')) return <FiMusic />;
    if (type.includes('zip') || type.includes('rar')) return <FiArchive />;
    if (type.includes('docx') || type.includes('doc')) return <FiFileText />;
    if (type.includes('txt') || type.includes('text')) return <FiFileText />;
    
    return <FiFile />;
  };

  const getFileTypeColor = (fileType) => {
    const type = fileType?.toLowerCase() || '';
    
    if (type.includes('pdf')) return '#EF4444';
    if (type.includes('image')) return '#10B981';
    if (type.includes('video')) return '#8B5CF6';
    if (type.includes('audio')) return '#F59E0B';
    if (type.includes('zip') || type.includes('rar')) return '#2c5f34';
    if (type.includes('docx') || type.includes('doc')) return '#2563EB';
    if (type.includes('txt') || type.includes('text')) return '#059669';
    
    return '#6B7280';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const canPreview = (fileType) => {
    const type = fileType?.toLowerCase() || '';
    return type.includes('pdf') || type.includes('image') || type.includes('text') || 
           type.includes('docx') || type.includes('doc') || type.includes('txt') ||
           type.includes('rtf') || type.includes('odt');
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          background: '#f8f9fa',
          color: '#6B7280',
          fontSize: '16px'
        }}>
          <LoadingSpinner size="small" />
          <span style={{ marginLeft: '12px' }}>Loading file preview...</span>
        </div>
      );
    }

    if (previewError || !fileUrl) {
      return (
        <UnsupportedFile>
          <FileIcon color="#6B7280">
            {getFileIcon(file.file_type)}
          </FileIcon>
          <h3>Preview Not Available</h3>
          <p>This file type cannot be previewed in the browser.</p>
          <p>Click download to view the file on your device.</p>
        </UnsupportedFile>
      );
    }

    const fileType = file.file_type?.toLowerCase() || '';

    if (fileType.includes('pdf')) {
      return (
        <PreviewContent>
          <iframe
            src={fileUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', borderRadius: '8px' }}
            title={file.title}
          />
        </PreviewContent>
      );
    }

    if (fileType.includes('image')) {
      return (
        <PreviewContent>
          <img
            src={fileUrl}
            alt={file.title}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: '8px'
            }}
          />
        </PreviewContent>
      );
    }

    if (fileType.includes('text') || fileType.includes('txt')) {
      return (
        <PreviewContent>
          <iframe
            src={fileUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', borderRadius: '8px' }}
            title={file.title}
          />
        </PreviewContent>
      );
    }

    // For document files (docx, doc, rtf, odt), use Office Online viewer
    if (fileType.includes('docx') || fileType.includes('doc') || fileType.includes('rtf') || fileType.includes('odt')) {
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
      
      return (
        <DocumentPreviewContent>
          <div style={{ 
            position: 'relative', 
            height: '100%', 
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <iframe
              src={officeViewerUrl}
              width="100%"
              height="100%"
              style={{ 
                border: 'none', 
                borderRadius: '8px',
                minHeight: isFullscreen ? '800px' : '600px',
                height: '100%',
                flex: 1
              }}
              title={file.title}
              onLoad={() => {
                // Hide loading indicator when iframe loads
                const loadingDiv = document.querySelector('.document-loading');
                if (loadingDiv) {
                  loadingDiv.style.display = 'none';
                }
              }}
              onError={() => {
                // Fallback to Google Docs viewer if Office Online fails
                const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;
                const iframe = document.querySelector('iframe[title="' + file.title + '"]');
                if (iframe) {
                  iframe.src = googleViewerUrl;
                }
              }}
            />
            <div 
              className="document-loading"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(255, 255, 255, 0.95)',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                fontSize: '16px',
                color: '#6B7280',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}
            >
              <LoadingSpinner size="small" />
              <div style={{ marginTop: '12px', fontWeight: '500' }}>Loading file preview...</div>
            </div>
          </div>
          <div style={{ 
            fontSize: '13px', 
            color: '#6B7280',
            textAlign: 'center',
            padding: '12px 16px',
            background: '#f8f9fa',
            borderTop: '1px solid #e5e7eb'
          }}>
            <span>Document preview - Read-only mode</span>
          </div>
        </DocumentPreviewContent>
      );
    }

    return (
      <UnsupportedFile>
        <FileIcon color={getFileTypeColor(file.file_type)}>
          {getFileIcon(file.file_type)}
        </FileIcon>
        <h3>Preview Not Supported</h3>
        <p>This file type ({file.file_type?.toUpperCase()}) cannot be previewed in the browser.</p>
        <p>This file is available for viewing only through the course interface.</p>
      </UnsupportedFile>
    );
  };


  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!isOpen || !file) return null;

  return (
    <FileViewerOverlay onClick={onClose}>
      <FileViewerContainer 
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: isFullscreen ? '100vw' : '1200px',
          maxHeight: isFullscreen ? '100vh' : '95vh',
          width: isFullscreen ? '100vw' : '100%',
          height: isFullscreen ? '100vh' : 'auto',
          borderRadius: isFullscreen ? '0' : '12px'
        }}
      >
        <FileViewerHeader>
          <FileInfo>
            <FileTitle>{file.title}</FileTitle>
            <FileDescription>{file.description || 'No description available'}</FileDescription>
            <FileMeta>
              <FileType color={getFileTypeColor(file.file_type)}>
                {file.file_type?.toUpperCase()}
              </FileType>
              <FileSize>{formatFileSize(file.file_size)}</FileSize>
              <span>📄 Course Material</span>
            </FileMeta>
          </FileInfo>
          <ActionButtons>
            {(file?.file_type?.toLowerCase().includes('docx') || file?.file_type?.toLowerCase().includes('doc') || file?.file_type?.toLowerCase().includes('pdf')) && (
              <DownloadButton 
                onClick={toggleFullscreen}
                style={{ background: '#F59E0B' }}
              >
                {isFullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
                {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              </DownloadButton>
            )}
            <CloseButton onClick={onClose}>
              <FiX />
            </CloseButton>
          </ActionButtons>
        </FileViewerHeader>

        <FileViewerBody>
          {renderPreview()}
        </FileViewerBody>

        <FileViewerFooter>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: '#6B7280',
            fontSize: '14px',
            flex: 1
          }}>
            <FileIcon color={getFileTypeColor(file.file_type)}>
              {getFileIcon(file.file_type)}
            </FileIcon>
            <span>{file.title}</span>
            <span>•</span>
            <span>{formatFileSize(file.file_size)}</span>
            <span>•</span>
            <span>{file.file_type?.toUpperCase()}</span>
          </div>
          
          {/* Navigation Controls */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginLeft: 'auto'
          }}>
            {hasPrevious && (
              <button 
                onClick={onPrevious}
                title="Previous"
                style={{
                  padding: '8px 16px',
                  background: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#E5E7EB'}
                onMouseLeave={(e) => e.target.style.background = '#F3F4F6'}
              >
                <FiChevronLeft />
                Previous
              </button>
            )}
            {hasNext && (
              <button 
                onClick={onNext}
                title="Next"
                style={{
                  padding: '8px 16px',
                  background: '#2c5f34',
                  border: '1px solid #2c5f34',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px',
                  color: 'white',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#4F46E5'}
                onMouseLeave={(e) => e.target.style.background = '#2c5f34'}
              >
                Next
                <FiChevronRight />
              </button>
            )}
          </div>
        </FileViewerFooter>
      </FileViewerContainer>
    </FileViewerOverlay>
  );
};

export default FileViewer;
